import { useMutation } from '@tanstack/react-query'
import { useId, useState } from 'react'
import { Link } from 'react-router-dom'

import { api, errorTrail, idempotencyKey, unwrap } from '../api/client'
import { t } from '../i18n'
import { ErrorNotice, Notice, Overlay } from '../ui'

/**
 * One tap, on every screen, with the context already filled in.
 *
 * The reason this is a fixed button rather than a menu item is the complaint
 * it answers: people on site do not file bug reports, they mention them once
 * and then work around them for a year. What they will do is press a button
 * that is always in the same corner.
 *
 * What goes with the report is everything the person would otherwise be asked
 * for and get wrong — the screen they were on, the project, the application
 * version, and the last few API failures — and it is shown to them before they
 * send it, because a report that quietly carries a device fingerprint is a
 * report people stop sending. Record content is never in the trail: the
 * client keeps the method, the path, the status and the message key, and
 * nothing of the body.
 */
export function ReportButton({
  projectId,
  route,
  appVersion,
  personName,
  moduleCode,
  triage = false,
}: {
  projectId: string | undefined
  route: string
  appVersion: string
  personName: string
  moduleCode: string
  /** Whether the feedback board is offered here as well as this person's own reports. */
  triage?: boolean
}) {
  const [open, setOpen] = useState(false)
  if (!projectId) return null
  return (
    <>
      <button type="button" className="report-button primary" onClick={() => setOpen(true)}>
        {t('feedback.button')}
      </button>
      {open ? (
        <ReportDialog
          projectId={projectId}
          route={route}
          appVersion={appVersion}
          personName={personName}
          moduleCode={moduleCode}
          triage={triage}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  )
}

function ReportDialog({
  projectId,
  route,
  appVersion,
  personName,
  moduleCode,
  triage,
  onClose,
}: {
  projectId: string
  route: string
  appVersion: string
  personName: string
  moduleCode: string
  triage: boolean
  onClose: () => void
}) {
  const titleId = useId()
  const [body, setBody] = useState('')
  // Praise is one of the four on purpose: a channel that only takes
  // complaints becomes a complaints box, and what a crew liked is worth as
  // much to the next release as what broke.
  const [kind, setKind] = useState<'bug' | 'suggestion' | 'question' | 'praise'>('bug')
  const [filed, setFiled] = useState<string | null>(null)
  const trail = errorTrail()

  const send = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.POST('/feedback/reports', {
          body: {
            project_id: projectId,
            body,
            kind,
            source: 'app',
            route,
            module_code: moduleCode,
            module_version: '',
            // A report from a help page carries the topic; this one is from a
            // screen, and saying so is what keeps the two apart in triage.
            help_topic_id: '',
            app_version: appVersion,
            device_platform: navigator.userAgent.slice(0, 200),
            was_online: navigator.onLine,
            // The trail is what turns "it did not work" into something a
            // triager can reproduce. It is capped by the client at eight.
            client_trail: trail.map((entry) => ({ ...entry })),
            idempotency_key: idempotencyKey('feedback'),
          },
        }),
      ),
    onSuccess: (report) => setFiled(report.reference || report.id),
  })

  return (
    <Overlay kind="dialog" titleId={titleId} onClose={onClose}>
      <header>
        <h2 id={titleId}>{t('feedback.title')}</h2>
        <button type="button" className="subtle" onClick={onClose}>
          {t('app.close')}
        </button>
      </header>
      <div className="body">
        {/* Where a report is found again once it has been sent. The button is
            the way in to feedback, so the lists are reached from here rather
            than from a second entry in the menu. */}
        <p className="feedback-lists">
          <Link to={`/p/${projectId}/feedback`} onClick={onClose}>
            {t('feedback.mine.open')}
          </Link>
          {triage ? (
            <>
              {' · '}
              <Link to={`/p/${projectId}/feedback/triage`} onClick={onClose}>
                {t('feedback.board.open')}
              </Link>
            </>
          ) : null}
        </p>
        {filed ? (
          <Notice tone="good">{t('feedback.sent', { number: filed })}</Notice>
        ) : (
          <>
            <div className="field">
              <label htmlFor="feedback-kind">{t('feedback.kind.label')}</label>
              <select
                id="feedback-kind"
                value={kind}
                onChange={(event) => {
                  const next = event.target.value
                  setKind(
                    next === 'suggestion' || next === 'question' || next === 'praise'
                      ? next
                      : 'bug',
                  )
                }}
              >
                <option value="bug">{t('feedback.kind.bug')}</option>
                <option value="suggestion">{t('feedback.kind.suggestion')}</option>
                <option value="question">{t('feedback.kind.question')}</option>
                <option value="praise">{t('feedback.kind.praise')}</option>
              </select>
            </div>
            <div className="field">
              <label htmlFor="feedback-body">{t('feedback.body.label')}</label>
              <textarea
                id="feedback-body"
                required
                value={body}
                placeholder={t('feedback.body.placeholder')}
                onChange={(event) => setBody(event.target.value)}
              />
            </div>
            {send.error ? <ErrorNotice error={send.error} /> : null}
          </>
        )}

        <details open={!filed}>
          <summary>{t('feedback.context.title')}</summary>
          <dl className="pairs">
            <dt>{t('feedback.context.route')}</dt>
            <dd className="mono">{route}</dd>
            <dt>{t('feedback.context.project')}</dt>
            <dd className="mono">{projectId}</dd>
            <dt>{t('feedback.context.person')}</dt>
            <dd>{personName}</dd>
            <dt>{t('feedback.context.version')}</dt>
            <dd className="mono">{appVersion}</dd>
            <dt>{t('feedback.context.errors')}</dt>
            <dd>
              {trail.length === 0 ? (
                <span className="muted">{t('feedback.context.none')}</span>
              ) : (
                <ul className="mono" style={{ margin: 0, paddingLeft: '1rem' }}>
                  {trail.map((entry, at) => (
                    <li key={at}>
                      {entry.status} {entry.method} {entry.path}
                      {entry.message_key ? ` · ${entry.message_key}` : ''}
                    </li>
                  ))}
                </ul>
              )}
            </dd>
          </dl>
        </details>
      </div>
      <footer>
        <button type="button" onClick={onClose}>
          {filed ? t('app.close') : t('app.cancel')}
        </button>
        {filed ? null : (
          <button
            type="button"
            className="primary"
            disabled={body.trim().length === 0 || send.isPending}
            onClick={() => send.mutate()}
          >
            {t('feedback.submit')}
          </button>
        )}
      </footer>
    </Overlay>
  )
}
